import { useQuery } from '@tanstack/react-query';
import { ProductService, productKeys } from '@client/services/productService';

/**
 * Hook to fetch a single product by identifier
 * @param {string|number} identifier - UUID or name of the product
 * @param {Object} [options={}] - Additional React Query options
 * @param {boolean} [options.enabled] - Whether the query should run
 * @param {number} [options.staleTime] - Time in ms before data becomes stale
 * @param {Function} [options.onSuccess] - Callback when query succeeds
 * @param {Function} [options.onError] - Callback when query fails
 * @returns {Object} React Query result object with data, loading, error states
 * @example
 * const { data: product, isLoading, error } = useProduct('123');
 */
export const useProduct = (identifier, options = {}) => {
  return useQuery({
    queryKey: productKeys.detail(identifier),
    queryFn: () => ProductService.getProduct(identifier),
    enabled: !!identifier,
    ...options,
  });
};

/**
 * Hook to fetch a list of products with optional filtering
 * @param {Object} [filters={}] - Query parameters for filtering the list
 * @param {string} [filters.search] - Search term for substance names
 * @param {string} [filters.category] - Filter by category
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Number of items per page
 * @param {Object} [options={}] - Additional React Query options
 * @param {boolean} [options.enabled] - Whether the query should run
 * @param {number} [options.staleTime] - Time in ms before data becomes stale
 * @param {Function} [options.onSuccess] - Callback when query succeeds
 * @param {Function} [options.onError] - Callback when query fails
 * @returns {Object} React Query result object with data array, loading, error states
 * @example
 * const { data: substances, isLoading } = useProducts({ search: 'aspirin' });
 */
export const useProducts = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: productKeys.filter(filters),
    queryFn: () => ProductService.filterProduct(filters),
    ...options,
  });
};

/**
 * Fetches culture tree data for an product
 * @param {string} productId - The product identifier
 * @param {Object} params - Query parameters for the API
 * @param {Object} options - React Query options
 * @returns {Object} Query result with tree data
 */
export const useProductCultureTree = (productId, params = {}, options = {}) => {
  return useQuery({
    queryKey: productKeys.cultureTree(productId, params),
    queryFn: () => ProductService.getCultureTree(productId, params),
    enabled: !!productId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};
